import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
    AdjustedDistance,
    Distance,
    LogisticsSiteMethod,
    RawDistance,
    RouteInferenceLegExplanation,
    ShippingMethod,
    ShippingRoute,
    ShippingSourceDestination,
} from '@lune-climate/lune'
import type { CalculatedRouteLocation } from '@lune-climate/lune/esm/models/CalculatedRouteLocation'
import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC } from 'react'

import Divider from '../Divider'
import { EmissionEstimationIcons } from '../emissionEstimationIcons'
import MapWithRoute, { RouteType } from '../map/MapWithRoute'

import { ExplanationStep } from './ExplanationSteps'
import StepWithInfoIcon from './StepWithInfoIcon'
import { getResolvedLegDestination, getResolvedLegSource, getSourceOrDestination } from './utils'

import 'mapbox-gl/dist/mapbox-gl.css'

export interface EmissionsRouteExplanationWithMapProps {
    mapboxAccessToken: string
    pdfStructure?: boolean
    distanceExplanation: ExplanationStep
    responseRoute: {
        source: CalculatedRouteLocation
        legs: {
            location: CalculatedRouteLocation
            rawDistance?: RawDistance
            distance: Distance
            adjustedDistance?: AdjustedDistance
        }[]
    }
    requestRoute: ShippingRoute | null
    requestMethod: ShippingMethod | LogisticsSiteMethod
    distance: Distance | undefined
    routeType: RouteType
    routeInferenceLegExplanation?: RouteInferenceLegExplanation | null
}

export const EmissionsRouteExplanationWithMap: FC<EmissionsRouteExplanationWithMapProps> = ({
    mapboxAccessToken,
    pdfStructure,
    distanceExplanation,
    responseRoute,
    requestRoute,
    requestMethod,
    distance,
    routeType,
    routeInferenceLegExplanation,
}) => {
    useLingui()

    /**
     * To be called in the context where `responseRoute` is defined. If it's defined we know
     * we have to have provided source and destination to the API so let's verify that and,
     * if it's the case, return a type-narrowed version of the `route`.
     *
     * Calling this function when `responseRoute` is null is a programming error and will
     * result in an exception.
     */
    function expectSourceDestinationRoute(route: ShippingRoute | null): ShippingSourceDestination {
        if (route === null || !('source' in route)) {
            throw new Error(
                `We expect the request route to be defined and to have source and destination`,
            )
        }
        return route
    }

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 9,
                flexDirection: 'column',
            }}
        >
            {!pdfStructure && (
                <>
                    <Divider />
                    <Box>
                        <Text variant={'h5'} sx={{ mb: 4 }}>
                            {t`Routing`}
                        </Text>
                        {distanceExplanation.description && (
                            <Box
                                sx={{
                                    mb: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                }}
                            >
                                {EmissionEstimationIcons.route}
                                <StepWithInfoIcon
                                    index={1}
                                    key={1}
                                    step={distanceExplanation}
                                    info={distanceExplanation.info ?? null}
                                />
                            </Box>
                        )}
                        <MapWithRoute
                            requestMethod={requestMethod}
                            // SAFETY: The environment variable may not be defined and then
                            // we'll just crash.
                            mapboxAccessToken={mapboxAccessToken}
                            sourceLabel={
                                getSourceOrDestination(
                                    getResolvedLegSource({
                                        responseRoute,
                                        routeInferenceDetails: routeInferenceLegExplanation,
                                    }) ?? expectSourceDestinationRoute(requestRoute).source,
                                    '',
                                ).label as string
                            }
                            destinationLabel={
                                getSourceOrDestination(
                                    getResolvedLegDestination({
                                        responseRoute,
                                        routeInferenceDetails: routeInferenceLegExplanation,
                                    }) ?? expectSourceDestinationRoute(requestRoute).destination,
                                    '',
                                ).label as string
                            }
                            routes={[
                                {
                                    type: routeType,
                                    coordinates: [
                                        responseRoute.source.coordinates,
                                        ...responseRoute.legs.map((l) => l.location.coordinates),
                                    ],
                                },
                            ]}
                            drawIntermediatePoints={routeType === 'representative'}
                            // SAFETY: The types don't express this but we know that when we get a route
                            // in response then we also have to get distance. Hence the type assertion.
                            distance={distance!}
                        />
                        {routeType === 'representative' && (
                            <Text variant="subtitle1" sx={{ mt: 2, color: 'text.secondary' }}>
                                {t`The route is an estimated path between waypoints. Waypoints (dots) and distances are accurate, while connecting lines are estimates and may appear to cross land`}
                            </Text>
                        )}
                    </Box>
                </>
            )}
        </Box>
    )
}

export default EmissionsRouteExplanationWithMap
